import { ref } from 'vue'

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'bizinix-crm-theme'

/** localStorage throws in Safari private mode and when storage is disabled. */
function readStoredTheme(): Theme | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return stored === 'light' || stored === 'dark' ? stored : null
  } catch {
    return null
  }
}

function prefersDark(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false
}

function applyTheme(theme: Theme): void {
  const root = document.documentElement
  root.classList.toggle('dark', theme === 'dark')
  root.style.colorScheme = theme
}

// Module-level, so every consumer shares one source of truth.
const theme = ref<Theme>(readStoredTheme() ?? (prefersDark() ? 'dark' : 'light'))

applyTheme(theme.value)

export function useTheme() {
  function setTheme(next: Theme): void {
    theme.value = next
    applyTheme(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Persisting is best-effort — the theme still applies for this session.
    }
  }

  function toggleTheme(): void {
    setTheme(theme.value === 'dark' ? 'light' : 'dark')
  }

  return { theme, toggleTheme }
}
